(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const getActiveQuickAddModal = () => document.querySelector('quick-add-modal[open]');

  const createOverlay = () => {
    const root = document.createElement('div');
    root.className = 'jr-zoom-overlay';
    root.hidden = true;
    root.innerHTML = `
      <div class="jr-zoom-overlay__backdrop" data-jr-zoom-close></div>
      <div class="jr-zoom-overlay__panel" role="dialog" aria-modal="true" aria-label="Zoomed image">
        <button type="button" class="jr-zoom-overlay__close" aria-label="Close zoom" data-jr-zoom-close>&times;</button>
        <div class="jr-zoom-overlay__viewport" data-jr-zoom-viewport>
          <img class="jr-zoom-overlay__img" alt="">
        </div>
        <div class="jr-zoom-overlay__hint">Scroll / pinch to zoom. Drag to pan.</div>
      </div>
    `;
    document.body.appendChild(root);
    return root;
  };

  const overlayEl = createOverlay();
  const viewportEl = overlayEl.querySelector('[data-jr-zoom-viewport]');
  const imgEl = overlayEl.querySelector('img.jr-zoom-overlay__img');
  const closeBtnEl = overlayEl.querySelector('[data-jr-zoom-close].jr-zoom-overlay__close');
  let lastActiveEl = null;

  const state = {
    scale: 1,
    x: 0,
    y: 0,
    isDragging: false,
    dragStart: null,
    pointers: new Map(),
    pinchStart: null,
  };

  const reset = () => {
    state.scale = 1;
    state.x = 0;
    state.y = 0;
    state.isDragging = false;
    state.dragStart = null;
    state.pointers.clear();
    state.pinchStart = null;
  };

  const apply = () => {
    if (!(imgEl instanceof HTMLImageElement)) return;
    if (!(viewportEl instanceof HTMLElement)) return;

    const scale = clamp(state.scale, 1, 5);
    state.scale = scale;

    const viewportRect = viewportEl.getBoundingClientRect();
    const imgRect = imgEl.getBoundingClientRect();

    // Clamp translate so the zoomed image doesn't drift into empty space.
    const extraX = Math.max(0, imgRect.width * scale - imgRect.width);
    const extraY = Math.max(0, imgRect.height * scale - imgRect.height);

    const maxX = extraX / 2 + Math.max(0, (viewportRect.width - imgRect.width) / 2);
    const maxY = extraY / 2 + Math.max(0, (viewportRect.height - imgRect.height) / 2);

    state.x = clamp(state.x, -maxX, maxX);
    state.y = clamp(state.y, -maxY, maxY);

    imgEl.style.transformOrigin = 'center center';
    imgEl.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${scale})`;
    imgEl.style.cursor = scale > 1 ? (state.isDragging ? 'grabbing' : 'grab') : 'zoom-in';
    imgEl.style.touchAction = 'none';
    imgEl.classList.toggle('jr-zoom-overlay__img--active', scale > 1);
  };

  const open = (src, alt = '') => {
    if (!(imgEl instanceof HTMLImageElement)) return;
    lastActiveEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    reset();
    imgEl.src = src;
    imgEl.alt = alt || '';
    overlayEl.hidden = false;
    try {
      document.body.classList.add('overflow-hidden');
    } catch {
      // ignore
    }
    try {
      closeBtnEl?.focus?.();
    } catch {
      // ignore
    }
    // Wait for layout, then ensure transform is applied.
    window.requestAnimationFrame(() => apply());
  };

  const close = () => {
    overlayEl.hidden = true;
    reset();
    if (imgEl instanceof HTMLImageElement) imgEl.removeAttribute('style');
    try {
      // Quick add already manages body scroll lock; don't hard-remove overflow-hidden if modal is open.
      if (!getActiveQuickAddModal()) document.body.classList.remove('overflow-hidden');
    } catch {
      // ignore
    }
    try {
      if (lastActiveEl && document.contains(lastActiveEl)) lastActiveEl.focus();
    } catch {
      // ignore
    }
    lastActiveEl = null;
  };

  overlayEl.addEventListener('click', (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;
    if (target.closest('[data-jr-zoom-close]')) close();
  });

  const getFocusable = () =>
    Array.from(
      overlayEl.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el instanceof HTMLElement && !el.hasAttribute('disabled') && !el.hidden);

  document.addEventListener('keydown', (event) => {
    if (overlayEl.hidden) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusable();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (event.shiftKey) {
      if (!active || active === first || !overlayEl.contains(active)) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (!active || active === last || !overlayEl.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  });

  // Zoom with wheel.
  viewportEl?.addEventListener(
    'wheel',
    (event) => {
      if (overlayEl.hidden) return;
      event.preventDefault();
      const delta = event.deltaY;
      const next = state.scale + (delta > 0 ? -0.18 : 0.18);
      state.scale = clamp(next, 1, 5);
      apply();
    },
    { passive: false }
  );

  const onPointerDown = (event) => {
    if (overlayEl.hidden) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    // No "snap" zoom on click/drag: only scroll wheel or pinch controls scale.
    if (state.scale > 1) {
      state.isDragging = true;
      state.dragStart = { x: event.clientX, y: event.clientY, startX: state.x, startY: state.y };
    }
    try {
      viewportEl?.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    apply();
  };

  const onPointerMove = (event) => {
    if (overlayEl.hidden) return;
    if (state.pointers.has(event.pointerId)) state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    // Pinch zoom with 2 pointers.
    if (state.pointers.size >= 2) {
      const pts = Array.from(state.pointers.values()).slice(0, 2);
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);
      if (!state.pinchStart) {
        state.pinchStart = { dist, scale: state.scale };
      } else {
        const ratio = dist / Math.max(1, state.pinchStart.dist);
        state.scale = clamp(state.pinchStart.scale * ratio, 1, 5);
      }
      apply();
      return;
    }

    if (!state.isDragging || !state.dragStart) return;
    state.x = state.dragStart.startX + (event.clientX - state.dragStart.x);
    state.y = state.dragStart.startY + (event.clientY - state.dragStart.y);
    apply();
  };

  const onPointerUp = (event) => {
    if (overlayEl.hidden) return;
    state.pointers.delete(event.pointerId);
    if (state.pointers.size < 2) state.pinchStart = null;
    state.isDragging = false;
    // Clear dragStart on the next tick so a click doesn't immediately toggle zoom after drag.
    window.setTimeout(() => {
      state.dragStart = null;
      apply();
    }, 0);
  };

  viewportEl?.addEventListener('pointerdown', onPointerDown);
  viewportEl?.addEventListener('pointermove', onPointerMove);
  viewportEl?.addEventListener('pointerup', onPointerUp);
  viewportEl?.addEventListener('pointercancel', onPointerUp);

  // Open zoom overlay from Quick Add: click the main gallery image (not thumbnails).
  document.addEventListener('click', (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    const qa = target.closest('quick-add-modal[open]');
    if (!qa) return;

    const viewer = target.closest('slider-component[id^="GalleryViewer"]');
    if (!viewer) return;

    const img = target.closest('img');
    if (!(img instanceof HTMLImageElement)) return;

    const src = img.currentSrc || img.src;
    if (!src) return;
    event.preventDefault();
    open(src, img.alt || '');
  });

  // Open zoom overlay from PDP: click the "open media" button (instead of opening the media modal).
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;

      if (target.closest('quick-add-modal')) return;

      const toggle = target.closest('button.product__media-toggle');
      if (!(toggle instanceof HTMLButtonElement)) return;

      const productInfo = toggle.closest('product-info');
      if (!productInfo) return;

      // Only hijack image openers.
      const opener = toggle.closest('modal-opener.product__modal-opener--image');
      if (!opener) return;

      const img =
        opener.querySelector('.product__media img') ||
        opener.querySelector('img') ||
        opener.parentElement?.querySelector('.product__media img');
      if (!(img instanceof HTMLImageElement)) return;

      const src = img.currentSrc || img.src;
      if (!src) return;

      event.preventDefault();
      event.stopPropagation();
      // Ensure we cancel ModalOpener's click handler.
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      open(src, img.alt || '');
    },
    true
  );
})();
